import {
    AdsGroupAd,
    FacebookAdsAdSetAd,
    GoogleAdsAdGroupAd,
} from '@superfunnel/types';
import googleIcon from '../../assets/images/google.png';
import { classNames } from '../../utils';

interface AdPreviewProps {
  ad: AdsGroupAd;
}

export const AdPreview = ({ ad }: AdPreviewProps) => {
  if (ad.provider === 'google') {
    return <GoogleAdPreview ad={ad} />;
  }

  if (ad.provider === 'facebook') {
    return <FacebookAdPreview ad={ad} />;
  }

  // Fallback for unknown providers
  return <div className="text-sm text-gray-600">Ad ID: {(ad as any).id}</div>;
};

const GoogleAdPreview = ({ ad }: { ad: GoogleAdsAdGroupAd }) => {
  const primaryHeadline =
    ad.headlines?.find(
      (h) => h.metrics?.impressions && h.metrics.impressions > 0,
    ) || ad.headlines?.[0];
  const primaryDescription =
    ad.descriptions?.find(
      (d) => d.metrics?.impressions && d.metrics.impressions > 0,
    ) || ad.descriptions?.[0];
  const displayUrl = ad.finalUrls?.[0]
    ? new URL(ad.finalUrls[0]).hostname
    : 'example.com';

  return (
    <div className={classNames('relative w-80 p-3', 'pb-4')}>
      <div className="flex items-start gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
          {/* <GlobeAltIcon className="h-4 w-4 text-green-600" /> */}
          <img src={googleIcon} alt="Google Ads" className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 text-xs text-green-700">
            <span>Ad</span>
            <span>•</span>
            <span className="truncate">{displayUrl}</span>
          </div>
          <div className="mt-1">
            <div
              className="cursor-pointer overflow-hidden text-ellipsis text-lg font-medium text-blue-600 hover:underline"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {primaryHeadline?.text || 'Sample Headline'}
            </div>
            <div
              className="mt-1 overflow-hidden text-ellipsis text-sm text-gray-700"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {primaryDescription?.text ||
                'Sample description text for the advertisement.'}
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 right-0 mt-3 text-xs text-gray-500">
        Ad ID: {(ad as any).id}
      </div>
    </div>
  );
};

const FacebookAdPreview = ({ ad }: { ad: FacebookAdsAdSetAd }) => {
  const primaryTitle =
    ad.titles?.find(
      (t) => t.metrics?.impressions && t.metrics.impressions > 0,
    ) || ad.titles?.[0];
  const primaryBody =
    ad.bodies?.find(
      (b) => b.metrics?.impressions && b.metrics.impressions > 0,
    ) || ad.bodies?.[0];
  const primaryDescription =
    ad.descriptions?.find(
      (d) => d.metrics?.impressions && d.metrics.impressions > 0,
    ) || ad.descriptions?.[0];

  return (
    <div className="relative w-80 pb-4">
      {/* Page header */}
      <div className="flex items-center gap-2 border-b border-gray-100 p-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500">
          <span className="text-xs font-semibold text-white">B</span>
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-gray-900">
            Business Page
          </div>
          <div className="text-xs text-gray-500">Sponsored</div>
        </div>
      </div>

      {/* Ad image placeholder */}
      <div className="flex aspect-video items-center justify-center bg-gray-100">
        {ad.adCreativeImageUrl ? (
          <img
            src={ad.adCreativeImageUrl}
            alt="Ad creative"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="text-sm text-gray-400">Ad Image</div>
        )}
      </div>

      {/* Ad content */}
      <div className="p-3">
        <div
          className="mb-2 overflow-hidden text-ellipsis text-sm text-gray-900"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {primaryBody?.text ||
            primaryTitle?.text ||
            'Sample ad text content that would appear in the Facebook ad.'}
        </div>

        {primaryDescription?.text && (
          <div
            className="mb-2 overflow-hidden text-ellipsis text-xs text-gray-600"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {primaryDescription.text}
          </div>
        )}

        {/* Call to action button */}
        <div className="mt-3">
          <button className="w-full rounded-sm bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
            {ad.callToActionType
              ?.replace(/_/g, ' ')
              .replace(/\b\w/g, (l) => l.toUpperCase()) || 'Learn More'}
          </button>
        </div>
      </div>

      {/* Ad ID */}
      <div className="absolute bottom-0 right-0 mt-3 text-xs text-gray-500">
        Ad ID: {(ad as any).id}
      </div>
    </div>
  );
};
